import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  WarningAmber as WarningAmberIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterListIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { REPS_COLORS } from '../utils/constants';

/**
 * Extract and flatten all warnings from OE snapshots into a chronological list.
 * Each warning entry includes the snapshot timestamp and scope.
 */
function extractWarningHistory(snapshots) {
  const history = [];

  for (const snapshot of snapshots) {
    if (!snapshot.warnings || snapshot.warnings.length === 0) continue;

    const timestamp = snapshot.calculatedAt?.toDate
      ? snapshot.calculatedAt.toDate()
      : snapshot.calculatedAt?.seconds
        ? new Date(snapshot.calculatedAt.seconds * 1000)
        : new Date(snapshot.calculatedAt || Date.now());

    for (const warning of snapshot.warnings) {
      history.push({
        id: `${snapshot.id}_${warning.quadrant}`,
        snapshotId: snapshot.id,
        timestamp,
        period: snapshot.period || '',
        scope: snapshot.scope || { type: 'organization' },
        quadrant: warning.quadrant,
        score: warning.score || 0,
        threshold: warning.threshold || 6,
        message: warning.message || '',
        responseCount: snapshot.responseCount || 0,
      });
    }
  }

  // Sort by timestamp descending (most recent first)
  history.sort((a, b) => b.timestamp - a.timestamp);

  return history;
}

/**
 * Analyze warning trends: is a quadrant improving or worsening over time?
 */
function analyzeWarningTrends(history) {
  const quadrantEntries = {};

  for (const entry of history) {
    if (!quadrantEntries[entry.quadrant]) {
      quadrantEntries[entry.quadrant] = [];
    }
    quadrantEntries[entry.quadrant].push(entry);
  }

  const trends = {};
  for (const [quadrant, entries] of Object.entries(quadrantEntries)) {
    // Entries are sorted newest first
    if (entries.length >= 2) {
      const latest = entries[0].score;
      const previous = entries[1].score;
      const diff = latest - previous;
      trends[quadrant] = {
        direction: diff > 0 ? 'worsening' : diff < 0 ? 'improving' : 'stable',
        diff: Math.abs(diff),
        occurrences: entries.length,
        latestScore: latest,
        previousScore: previous,
      };
    } else {
      trends[quadrant] = {
        direction: 'new',
        diff: 0,
        occurrences: 1,
        latestScore: entries[0].score,
        previousScore: null,
      };
    }
  }

  return trends;
}

function formatDate(date) {
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function quadrantLabel(q) {
  return q ? q.charAt(0).toUpperCase() + q.slice(1) : '';
}

function WarningHistory({ snapshots = [], scopeLabel = 'All sites', periodLabel = '' }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [quadrantFilter, setQuadrantFilter] = useState('all');

  // Extract warning history from snapshots
  const warningHistory = useMemo(
    () => extractWarningHistory(snapshots),
    [snapshots]
  );

  // Analyze trends
  const trends = useMemo(
    () => analyzeWarningTrends(warningHistory),
    [warningHistory]
  );

  // Filter by quadrant
  const filteredHistory = useMemo(() => {
    if (quadrantFilter === 'all') return warningHistory;
    return warningHistory.filter((w) => w.quadrant === quadrantFilter);
  }, [warningHistory, quadrantFilter]);

  // Get unique quadrants for filter dropdown
  const affectedQuadrants = useMemo(
    () => [...new Set(warningHistory.map((w) => w.quadrant))],
    [warningHistory]
  );

  // Paginate
  const paginatedHistory = filteredHistory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (warningHistory.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          No warnings for {scopeLabel}{periodLabel ? ` — ${periodLabel.toLowerCase()}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A warning is recorded on a scope&apos;s snapshot the moment one of its REPS scores rises above the threshold
          (Settings → DREW → Warning Thresholds). None did here in this period. Try a specific site, department or NWT —
          the enterprise average can stay green while one unit is red — or a longer period.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Summary - Trend Cards */}
      {Object.keys(trends).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Warning Trends by Quadrant
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {Object.entries(trends).map(([quadrant, trend]) => {
              const color = REPS_COLORS[quadrant] || '#757575';
              const TrendIcon =
                trend.direction === 'improving'
                  ? TrendingDownIcon
                  : trend.direction === 'worsening'
                    ? TrendingUpIcon
                    : TimelineIcon;
              const trendColor =
                trend.direction === 'improving'
                  ? 'success.main'
                  : trend.direction === 'worsening'
                    ? 'error.main'
                    : 'text.secondary';
              const trendLabel =
                trend.direction === 'improving'
                  ? `Improving (-${trend.diff.toFixed(1)})`
                  : trend.direction === 'worsening'
                    ? `Worsening (+${trend.diff.toFixed(1)})`
                    : trend.direction === 'new'
                      ? 'New Warning'
                      : 'Stable';

              return (
                <Paper
                  key={quadrant}
                  variant="outlined"
                  sx={{
                    p: 2,
                    minWidth: 180,
                    borderLeft: `4px solid ${color}`,
                    flex: '1 1 180px',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}
                  >
                    {quadrantLabel(quadrant)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <TrendIcon sx={{ fontSize: 18, color: trendColor }} />
                    <Typography variant="body2" sx={{ color: trendColor, fontWeight: 600 }}>
                      {trendLabel}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {trend.occurrences} warning{trend.occurrences !== 1 ? 's' : ''} recorded
                    {trend.latestScore != null && ` | Latest: ${trend.latestScore.toFixed(1)}`}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FilterListIcon sx={{ color: 'text.secondary' }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Quadrant</InputLabel>
          <Select
            value={quadrantFilter}
            label="Quadrant"
            onChange={(e) => {
              setQuadrantFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Quadrants</MenuItem>
            {affectedQuadrants.map((q) => (
              <MenuItem key={q} value={q}>
                {quadrantLabel(q)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filteredHistory.length} warning{filteredHistory.length !== 1 ? 's' : ''} total
        </Typography>
      </Box>

      {/* Warning History Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Quadrant</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Threshold</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Exceeded By</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Responses</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedHistory.map((entry) => {
              const color = REPS_COLORS[entry.quadrant] || '#757575';
              const exceeded = entry.score - entry.threshold;

              return (
                <TableRow
                  key={entry.id}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Tooltip title={formatDate(entry.timestamp)}>
                      <Typography variant="body2">
                        {formatDate(entry.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                      label={quadrantLabel(entry.quadrant)}
                      size="small"
                      sx={{
                        bgcolor: `${color}15`,
                        color,
                        fontWeight: 600,
                        borderColor: color,
                      }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 700, color: '#c62828' }}
                    >
                      {entry.score.toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: '"Roboto Mono", monospace' }}
                    >
                      {entry.threshold}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`+${exceeded.toFixed(1)}`}
                      size="small"
                      color="error"
                      sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {entry.period || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {entry.responseCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredHistory.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

export default WarningHistory;
